import { countryFlag } from './country'

/**
 * World-region flags, mirroring the backend Common.Enums.CountryRegions.
 * A post can target a whole region instead of a single country; countries
 * carry a `regions` membership mask made of these bits.
 */
export const REGION_FLAGS = [
  { bit: 1, key: 'international', emoji: '🌐' },
  { bit: 2, key: 'europe', emoji: '🌍' },
  { bit: 4, key: 'europeanUnion', emoji: '🇪🇺' },
  { bit: 8, key: 'asia', emoji: '🌏' },
  { bit: 16, key: 'middleEast', emoji: '🌍' },
  { bit: 32, key: 'africa', emoji: '🌍' },
  { bit: 64, key: 'northAmerica', emoji: '🌎' },
  { bit: 128, key: 'centralAmerica', emoji: '🌎' },
  { bit: 256, key: 'caribbean', emoji: '🏝️' },
  { bit: 512, key: 'southAmerica', emoji: '🌎' },
  { bit: 1024, key: 'southAsia', emoji: '🌏' },
  { bit: 2048, key: 'southeastAsia', emoji: '🌏' },
  { bit: 4096, key: 'eastAsia', emoji: '🌏' },
  { bit: 8192, key: 'centralAsia', emoji: '🌏' },
  { bit: 16384, key: 'australiaNewZealand', emoji: '🌏' },
  { bit: 32768, key: 'pacificIslands', emoji: '🌊' },
] as const

export type RegionFlag = (typeof REGION_FLAGS)[number]

export function regionByBit(bit: number): RegionFlag | undefined {
  return REGION_FLAGS.find((region) => region.bit === bit)
}

/**
 * The audience shown beside a page's or community's handle in its header: a
 * country gives just its flag, matching a user profile, a region gives the key
 * for its spelled-out name, and neither gives null so the header shows nothing.
 * Regions are spelled out because most of them share a generic globe above
 * (Europe, the Middle East and Africa are all the same emoji), which would tell
 * the reader nothing on its own.
 *
 * The region is returned as a key rather than a translated string so the caller
 * translates it with its own `t`: i18next's TFunction carries literal key types
 * that a `(key: string) => string` parameter cannot accept. Read it as
 * `const audience = parts && ('flag' in parts ? parts.flag : t(`regions.${parts.regionKey}`))`.
 */
export function audienceHeader(
  region: number | null | undefined,
  countryCode: string | null | undefined,
): { regionKey: RegionFlag['key'] } | { flag: string } | null {
  const found = region == null ? undefined : regionByBit(region)
  if (found) return { regionKey: found.key }
  return countryCode && countryCode.length === 2 ? { flag: countryFlag(countryCode) } : null
}
